import { useCallback, useRef, useState } from 'react';
import { CHAT_DAY_LIST_DRESS, listNameFrom, type CalendarEventDto } from '@chat-emp/shared';

import { isChatDayList, refreshLists, useLists } from '../inbox/useLists.js';
import { ApiRequestError } from '../lib/api.js';
import { addChatDayGuests, createChatDay } from '../lib/facade.js';
import { useSession } from '../session/SessionProvider.js';
import { draftSpan, type EventDraft, type Guest } from './draft.js';
import { durationMinutes } from './event-rules.js';
import { sendChatDayInvites, type ChatDayInvite } from './invites.js';
import { refreshEvents } from './useEvents.js';

/**
 * Why a booking did not happen, in words the employer can act on.
 *
 * `retryable` comes off the BFF envelope and decides whether the message invites
 * another press: a calendar that was briefly unreachable is worth pressing Save
 * again, a time that has since been taken is not — that one needs the draft
 * changed first.
 */
export interface BookingFailure {
  message: string;
  retryable: boolean;
}

const GENERIC = 'This Chat Day could not be booked.';

/**
 * A guest whose chat has gone is the one failure with a fix attached.
 *
 * The BFF answers 404 when a channel is not the caller's any more — the chat was
 * ended, or the applicant moved. The envelope's own message is about a
 * conversation; this one is about the guest list, which is the thing on screen
 * and the thing to change.
 */
const GUEST_GONE = 'One of your guests is no longer reachable. Remove them and try again.';

function failureOf(cause: unknown): BookingFailure {
  if (cause instanceof ApiRequestError) {
    if (cause.code === 'guest_unavailable') return { message: GUEST_GONE, retryable: false };
    return { message: cause.message || GENERIC, retryable: cause.retryable };
  }
  // Not from the façade at all — a bug here, or the network dropping under the
  // fetch. Worth another press either way.
  return { message: GENERIC, retryable: true };
}

/**
 * What happened to the invites once the Chat Day was booked.
 *
 * Reported rather than swallowed, because the two halves can come apart: the
 * appointment exists whether or not every message landed, and an employer who
 * believes twenty-four people were told when twenty-one were finds out on the
 * day, from the three who never came.
 */
export interface InviteReport {
  /** Guests who got the invite, and the covering note where there was one. */
  sent: number;
  total: number;
  /** When adding to a saved event: how many of `total` the calendar took. */
  added?: number;
}

/** The card for a report whose numbers disagree. */
export function reportCopy(report: InviteReport): { title: string; detail: string } {
  if (report.added === undefined) {
    return {
      title: 'Chat Day booked, but not everyone was told',
      detail: `${report.sent} of ${report.total} guests got the invite. Message the rest from their chats.`,
    };
  }
  if (report.added === report.total) {
    return {
      title: 'Guests added, but not everyone was told',
      detail: `${report.sent} of ${report.added} new guests got the invite. Message the rest from their chats.`,
    };
  }
  const told = report.sent < report.added ? ` ${report.sent} got the invite.` : '';
  return {
    title: 'Not every guest was added',
    detail: `${report.added} of ${report.total} guests were added.${told} Try adding the rest again.`,
  };
}

export interface Booking {
  /** In flight. Save shows a pending state and refuses a second press. */
  saving: boolean;
  /** The last failure, or null. Cleared when a fresh attempt starts. */
  error: BookingFailure | null;
  /**
   * Book it and tell the guests. Resolves with what reached them, or null when
   * nothing was booked at all — in which case nothing was sent either.
   */
  book: (draft: EventDraft, note: string) => Promise<InviteReport | null>;
  /** Add guests to a saved event, and invite only them. Null when nobody was added. */
  addGuests: (
    event: CalendarEventDto,
    guests: Guest[],
    note: string,
  ) => Promise<InviteReport | null>;
  /** Forget the last failure — for a draft that is being started over. */
  reset: () => void;
}

/**
 * Booking the Chat Day that is on screen.
 *
 * Separate from `useChatDay`, which is the draft and stays pure: one holds what
 * the employer has typed, this one holds what happened when they pressed Save.
 *
 * The mock this form was ported from closed the modal first and ran the write in
 * a void-ed async block with no catch — an affordance of an in-memory store that
 * cannot fail. Here a booking that fails upstream would leave the employer
 * looking at a calendar their Chat Day never reached, with nothing said. So the
 * modal stays open until the write returns, and only success closes it.
 */
export function useBooking(): Booking {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<BookingFailure | null>(null);
  // Where the invite's Accept / Decline post. The BFF names it — this app must
  // not hold a chat-js hostname — and empty means chat-js has not shipped its
  // handler yet, which `sendChatDayInvites` reads as "send without buttons".
  const { endpoints } = useSession();
  // The store a booked Chat Day files its guests into. The hook rather than a
  // bare module call, and that is the point: subscribing here arms the one-time
  // fetch of the user's lists when the view mounts, long before any booking, so
  // the row this adds can never be the first thing in a store whose hydrate is
  // still in flight — which is the one case where that hydrate drops the
  // server's answer (see `useLists`).
  const { lists, create: createList, addTo } = useLists();
  /**
   * The double-submit guard, and it is a ref rather than the state above
   * deliberately: `saving` is only true on the next render, so two presses
   * landing before React re-renders would both read false and book two Chat
   * Days. There is no unbooking in this scope, so the guard cannot be one frame
   * late. The disabled button is the visible half of the same rule.
   */
  const inFlight = useRef(false);

  /** The double-submit guard, the saving flag and the failure, around one write. */
  const run = useCallback(
    async (write: () => Promise<InviteReport>): Promise<InviteReport | null> => {
      if (inFlight.current) return null;
      inFlight.current = true;
      setSaving(true);
      setError(null);
      try {
        const report = await write();
        // Re-read rather than pushing the returned event into the store: what the
        // calendar draws is then what the server stored, including its status.
        await refreshEvents();
        return report;
      } catch (cause) {
        setError(failureOf(cause));
        return null;
      } finally {
        inFlight.current = false;
        setSaving(false);
      }
    },
    [],
  );

  /**
   * Tell the guests, and say how many heard. The event EXISTS by now, so a throw
   * here must not reach `run`'s catch: it would read as "not saved" and invite a
   * second press that books a second Chat Day nobody can take back.
   */
  const invite = useCallback(
    async (event: CalendarEventDto, guests: Guest[], note: string): Promise<number> => {
      try {
        const { done } = await sendChatDayInvites({
          appointmentId: appointmentIdOf(event),
          title: event.title,
          startsAt: event.startsAt,
          endsAt: event.endsAt,
          location: event.location,
          ...(event.description ? { description: event.description } : {}),
          ...(note.trim() ? { message: note.trim() } : {}),
          invitees: invitesFor(guests, event),
          responderBaseUrl: endpoints.chatDayResponder,
        });
        return done;
      } catch (cause) {
        console.error('[event] Saved, but the invites did not go out', cause);
        return 0;
      }
    },
    [endpoints.chatDayResponder],
  );

  const book = useCallback(
    (draft: EventDraft, note: string): Promise<InviteReport | null> => {
      const span = draftSpan(draft);
      // Save is not reachable on a draft with no span — `missing` marks the gaps
      // and returns before this. Belt and braces.
      if (!span) return Promise.resolve(null);

      return run(async () => {
        const description = draft.description.trim();
        const title = draft.title.trim();
        const channelIds = draft.invitees.map((guest) => guest.channelId);
        // The BFF turns these channels back into people, having first proved
        // this employer is in every one of them.
        const event = await createChatDay({
          title,
          startsAt: span.startsAt,
          durationMinutes: durationMinutes(span),
          ...(description ? { description } : {}),
          channelIds,
        });

        // The guest list, kept as a list (AI-2744). Unawaited on purpose: `create`
        // paints the row now and syncs behind itself, and nothing after a booking
        // that landed may delay the invites or report it as a failure.
        createList({ name: listNameFrom(title), ...CHAT_DAY_LIST_DRESS }, channelIds);

        const sent = await invite(event, draft.invitees, note);
        return { sent, total: draft.invitees.length };
      });
    },
    [run, invite, createList],
  );

  const addGuests = useCallback(
    (event: CalendarEventDto, guests: Guest[], note: string): Promise<InviteReport | null> => {
      if (guests.length === 0) return Promise.resolve(null);

      return run(async () => {
        const result = await addChatDayGuests(event.id, {
          channelIds: guests.map((guest) => guest.channelId),
        });
        const added = new Set(result.added);
        const newGuests = guests.filter((guest) => added.has(guest.channelId));

        // A list names its event only by title, so an ambiguous match is left alone.
        const listName = listNameFrom(event.title);
        const matches = lists.filter((list) => isChatDayList(list) && list.name === listName);
        if (matches.length === 1 && result.added.length > 0) addTo(matches[0]!.id, result.added);
        // A marketing Chat Day's list is filed on the server; it only needs a re-read here.
        if (result.listsChanged) void refreshLists();

        const sent = await invite(result.event, newGuests, note);
        return {
          sent,
          total: result.added.length + result.failed.length,
          added: result.added.length,
        };
      });
    },
    [run, invite, lists, addTo],
  );

  const reset = useCallback(() => setError(null), []);

  return { saving, error, book, addGuests, reset };
}

/**
 * Who to invite, and where — the guests the employer picked, given each one's
 * invitee row id where the saved event names them. Driven from the guests, not
 * the event, so a short echo costs a guest their buttons rather than their invite.
 */
function invitesFor(guests: Guest[], event: CalendarEventDto): ChatDayInvite[] {
  const rowIds = new Map(event.invitees.map((invitee) => [invitee.userId, invitee.id]));
  return guests.map((guest) => {
    const inviteeId = rowIds.get(guest.id);
    return {
      channelId: guest.channelId,
      name: guest.name,
      ...(inviteeId !== undefined ? { inviteeId } : {}),
    };
  });
}

/**
 * The appointment the buttons answer against.
 *
 * `CalendarEventDto.id` is a string because that is what an id is to everything
 * that draws it; the CTA payload needs the number the upstream keys on. NaN is
 * not a thing anything upstream would accept, so it reaches `inviteCta` as an
 * appointment with no valid id — which, like an absent invitee row, means the
 * invite goes out without a button row instead of with a broken one.
 */
function appointmentIdOf(event: CalendarEventDto): number {
  return Number(event.id);
}
